import { useMemo } from 'react'
import { ListFilterDrawer } from '@/components/ui/ListFilterDrawer'
import { SearchableSelect } from '@/components/ui/SearchableSelect'
import { formatDateForApi } from '../../utils/residentDateUtils'
import type {
  ResidentDateRange,
  ResidentListFilters,
} from '../../types/resident'
import { ResidentLeaseStartDateRangeField } from './ResidentLeaseStartDateRangeField'
import type { ResidentFiltersProps } from '../../types'

const RESIDENT_STATUS_OPTIONS = [
  { value: 'current', label: 'Current Residents' },
  { value: '2', label: 'Past Residents' },
  { value: '1', label: 'All Residents' },
] as const

const ADVANCED_SEARCH_OPTIONS = [
  { value: 'yardi_properties', label: 'Yardi Properties' },
] as const

/** Map UI select value ↔ API `past_customer` (empty string = Current Residents). */
function pastCustomerToSelectValue(pastCustomer: unknown): string {
  if (pastCustomer === '' || pastCustomer === null || pastCustomer === undefined) {
    return pastCustomer === '' ? 'current' : ''
  }
  return String(pastCustomer)
}

function selectValueToPastCustomer(value: string): string {
  if (value === 'current') return ''
  return value
}

export function ResidentFilters({
  query,
  companyOptions,
  propertyOptions,
  salesOptions,
  payStatusOptions,
  residentRentalStatusOptions,
  residentRentalProgressOptions,
  reimbursementTypeOptions,
  reimbursementRentalStatusOptions,
  reimbursementRentalProgressOptions,
  isCompanyLocked,
  isPropertyLocked,
  canUseAdvancedSearch,
  isOpen,
  loading,
  onApply,
  onReset,
  onClose,
  onCompanySearch,
  onPropertySearch,
}: ResidentFiltersProps) {
  const update = (key: keyof ResidentListFilters, value: unknown) => {
    onApply({ [key]: value })
  }

  const handleLeaseStartChange = (start: Date | null, end: Date | null) => {
    if (!start && !end) {
      onApply({
        lease_start_daterange: undefined,
        lease_start_fromDt: '',
        lease_start_toDt: '',
      })
      return
    }
    /** Calendar emits partial ranges while selecting; keep prior filter until both ends are chosen. */
    if (!start || !end) return

    const range: ResidentDateRange = {
      lease_start_fromDt: formatDateForApi(start),
      lease_start_toDt: formatDateForApi(end),
    }
    onApply({
      lease_start_daterange: range,
      lease_start_fromDt: range.lease_start_fromDt,
      lease_start_toDt: range.lease_start_toDt,
    })
  }

  const salesSelectOptions = useMemo(
    () => salesOptions.map((user) => ({ value: String(user.value), label: user.label })),
    [salesOptions],
  )

  const residentStatusSelectValue = pastCustomerToSelectValue(query.past_customer)

  return (
    <ListFilterDrawer
      isOpen={isOpen}
      onClose={onClose}
      onReset={onReset}
      testIdPrefix="resident-list"
    >
      <div className="space-y-2" data-testid="resident-list-filters-company-field">
        <FilterLabel>Company</FilterLabel>
        <SearchableSelect
          options={companyOptions}
          value={query.companyId || ''}
          onChange={async (value) => {
            const newCompanyId = String(value)
            const selectedCompany = companyOptions.find(
              (opt) => String(opt.value) === newCompanyId,
            )
            const companyName = selectedCompany ? selectedCompany.label : undefined
            const properties = await onPropertySearch(undefined, newCompanyId)
            // Angular `loadProperty`: auto-set only when exactly one property is
            // returned; otherwise keep a still-valid propertyId, else clear.
            let propertyId = ''
            let propertyName: string | undefined
            if (properties && properties.length === 1) {
              propertyId = String(properties[0].value)
              propertyName = properties[0].label
            } else if (
              query.propertyId &&
              properties?.some((option) => String(option.value) === String(query.propertyId))
            ) {
              propertyId = String(query.propertyId)
              propertyName = query.property_name
            }
            onApply({
              companyId: newCompanyId,
              company_name: companyName,
              propertyId,
              property_name: propertyName,
            })
          }}
          onSearch={async (search) => {
            const options = await onCompanySearch(search)
            if (options && options.length === 1) {
              const singleComp = options[0]
              const properties = await onPropertySearch(undefined, String(singleComp.value))
              const singleProp = properties && properties.length === 1 ? properties[0] : undefined
              onApply({
                companyId: String(singleComp.value),
                company_name: singleComp.label,
                ...(singleProp
                  ? { propertyId: String(singleProp.value), property_name: singleProp.label }
                  : {}),
              })
            }
          }}
          onPasteSearch={async (pasted) => {
            const options = await onCompanySearch(pasted, true)
            if (options && options.length === 1) {
              const singleComp = options[0]
              const properties = await onPropertySearch(undefined, String(singleComp.value))
              const singleProp = properties && properties.length === 1 ? properties[0] : undefined
              onApply({
                companyId: String(singleComp.value),
                company_name: singleComp.label,
                ...(singleProp
                  ? { propertyId: String(singleProp.value), property_name: singleProp.label }
                  : {}),
              })
            }
          }}
          onOpen={() => onCompanySearch()}
          disabled={isCompanyLocked || loading}
          placeholder="All Company"
          searchPlaceholder="Search By Company Name"
          invokeSearchOnClose={false}
          className={getFilterClass(query.companyId)}
        />
      </div>

      <div className="space-y-2" data-testid="resident-list-filters-property-field">
        <FilterLabel>Property</FilterLabel>
        <SearchableSelect
          options={propertyOptions}
          value={query.propertyId || ''}
          onChange={(value) => {
            const newPropertyId = String(value)
            const selectedProperty = propertyOptions.find(
              (opt) => String(opt.value) === newPropertyId,
            )
            onApply({
              propertyId: newPropertyId,
              property_name: selectedProperty ? selectedProperty.label : undefined,
            })
          }}
          onSearch={async (search) => {
            const options = await onPropertySearch(search, query.companyId)
            if (options && options.length === 1) {
              const singleProp = options[0]
              onApply({
                propertyId: String(singleProp.value),
                property_name: singleProp.label,
              })
            }
          }}
          onPasteSearch={async (pasted) => {
            const options = await onPropertySearch(pasted, query.companyId, true)
            if (options && options.length === 1) {
              const singleProp = options[0]
              onApply({
                propertyId: String(singleProp.value),
                property_name: singleProp.label,
              })
            }
          }}
          onOpen={() => onPropertySearch(undefined, query.companyId)}
          disabled={isPropertyLocked || loading}
          placeholder="All Properties"
          searchPlaceholder="Search By Property Name"
          invokeSearchOnClose={false}
          className={getFilterClass(query.propertyId)}
        />
      </div>

      <div
        className="space-y-2"
        data-testid="resident-list-filters-pay-status-field"
      >
        <FilterLabel>Rent Paid Status</FilterLabel>
        <SearchableSelect
          options={payStatusOptions}
          value={
            Array.isArray(query.pay_status)
              ? query.pay_status
              : query.pay_status
                ? [query.pay_status]
                : []
          }
          onChange={(value) => update('pay_status', value)}
          multiple
          selectedChipsLayout="flex-wrap"
          disabled={loading}
          placeholder="Rent Paid Status"
          className={getFilterClass(query.pay_status)}
        />
      </div>

      <div
        className="space-y-2"
        data-testid="resident-list-filters-resident-rental-status-field"
      >
        <FilterLabel>Resident Rental Status</FilterLabel>
        <SearchableSelect
          options={residentRentalStatusOptions}
          value={
            Array.isArray(query.resident_rental_status)
              ? query.resident_rental_status
              : query.resident_rental_status
                ? [query.resident_rental_status]
                : []
          }
          onChange={(value) => update('resident_rental_status', value)}
          multiple
          selectedChipsLayout="flex-wrap"
          disabled={loading}
          placeholder="Resident Rental Status"
          className={getFilterClass(query.resident_rental_status)}
        />
      </div>

      <div
        className="space-y-2"
        data-testid="resident-list-filters-resident-rental-progress-field"
      >
        <FilterLabel>Resident Rental Progress</FilterLabel>
        <SearchableSelect
          options={residentRentalProgressOptions}
          value={
            Array.isArray(query.resident_rental_progress)
              ? query.resident_rental_progress
              : query.resident_rental_progress
                ? [query.resident_rental_progress]
                : []
          }
          onChange={(value) => update('resident_rental_progress', value)}
          multiple
          selectedChipsLayout="flex-wrap"
          disabled={loading}
          placeholder="Resident Rental Progress"
          className={getFilterClass(query.resident_rental_progress)}
        />
      </div>

      <div
        className="space-y-2"
        data-testid="resident-list-filters-reimbursement-type-field"
      >
        <FilterLabel>Reimbursement Type</FilterLabel>
        <SearchableSelect
          options={reimbursementTypeOptions}
          value={
            Array.isArray(query.reimbursement_type)
              ? query.reimbursement_type
              : query.reimbursement_type
                ? [query.reimbursement_type]
                : []
          }
          onChange={(value) => update('reimbursement_type', value)}
          multiple
          selectedChipsLayout="flex-wrap"
          disabled={loading}
          placeholder="Reimbursement Type"
          className={getFilterClass(query.reimbursement_type)}
        />
      </div>

      <div
        className="space-y-2"
        data-testid="resident-list-filters-reimbursement-rental-status-field"
      >
        <FilterLabel>Reimbursement Status</FilterLabel>
        <SearchableSelect
          options={reimbursementRentalStatusOptions}
          value={
            Array.isArray(query.reimbursement_rental_status)
              ? query.reimbursement_rental_status
              : query.reimbursement_rental_status
                ? [query.reimbursement_rental_status]
                : []
          }
          onChange={(value) => update('reimbursement_rental_status', value)}
          multiple
          selectedChipsLayout="flex-wrap"
          disabled={loading}
          placeholder="Reimbursement Status"
          className={getFilterClass(query.reimbursement_rental_status)}
        />
      </div>

      <div
        className="space-y-2"
        data-testid="resident-list-filters-reimbursement-rental-progress-field"
      >
        <FilterLabel>Reimbursement Progress</FilterLabel>
        <SearchableSelect
          options={reimbursementRentalProgressOptions}
          value={
            Array.isArray(query.reimbursement_rental_progress)
              ? query.reimbursement_rental_progress
              : query.reimbursement_rental_progress
                ? [query.reimbursement_rental_progress]
                : []
          }
          onChange={(value) => update('reimbursement_rental_progress', value)}
          multiple
          selectedChipsLayout="flex-wrap"
          disabled={loading}
          placeholder="Reimbursement Progress"
          className={getFilterClass(query.reimbursement_rental_progress)}
        />
      </div>

      <div
        className="space-y-2"
        data-testid="resident-list-filters-past-customer-field"
      >
        <FilterLabel>Resident Status</FilterLabel>
        <SearchableSelect
          options={[...RESIDENT_STATUS_OPTIONS]}
          value={residentStatusSelectValue}
          onChange={(value) => {
            const raw = Array.isArray(value) ? (value[0] ?? '') : value
            update('past_customer', selectValueToPastCustomer(String(raw || '')))
          }}
          searchable={false}
          disabled={loading}
          placeholder="Resident Status"
          triggerDataTestId="resident-list-filters-past-customer-select"
          className={getFilterClass(residentStatusSelectValue)}
        />
      </div>

      <div
        className="space-y-2"
        data-testid="resident-list-filters-lease-start-field"
      >
        <FilterLabel>Lease Start Date</FilterLabel>
        <ResidentLeaseStartDateRangeField
          fromDt={query.lease_start_fromDt}
          toDt={query.lease_start_toDt}
          disabled={loading}
          onRangeChange={handleLeaseStartChange}
          className={getFilterClass(query.lease_start_fromDt || query.lease_start_toDt)}
        />
      </div>

      <div
        className="space-y-2"
        data-testid="resident-list-filters-sales-exec-field"
      >
        <FilterLabel>Sales Exec</FilterLabel>
        <SearchableSelect
          options={salesSelectOptions}
          value={query.sales_exec_id || ''}
          onChange={(value) => {
            const raw = Array.isArray(value) ? (value[0] ?? '') : value
            update('sales_exec_id', String(raw || ''))
          }}
          disabled={loading}
          placeholder="Select Sales Exec"
          searchPlaceholder="Search Sales Exec"
          triggerDataTestId="resident-list-filters-sales-exec-select"
          className={getFilterClass(query.sales_exec_id)}
        />
      </div>

      {canUseAdvancedSearch ? (
        <div
          className="space-y-2"
          data-testid="resident-list-filters-advanced-search-field"
        >
          <FilterLabel>Advanced Search</FilterLabel>
          <SearchableSelect
            options={[...ADVANCED_SEARCH_OPTIONS]}
            value={query.advance_search || ''}
            onChange={(value) => {
              const raw = Array.isArray(value) ? (value[0] ?? '') : value
              update('advance_search', String(raw || ''))
            }}
            searchable={false}
            disabled={loading}
            placeholder="Select Advance Option"
            triggerDataTestId="resident-list-filters-advanced-search-select"
            className={getFilterClass(query.advance_search)}
          />
        </div>
      ) : null}
    </ListFilterDrawer>
  )
}

function FilterLabel({ children }: { children: string }) {
  return (
    <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500">
      {children}
    </label>
  )
}

function isFilterValueSelected(value: unknown): boolean {
  if (value === null || value === undefined || value === '') return false
  if (Array.isArray(value)) return value.length > 0
  if (value === '0') return false // Resident Status placeholder
  return true
}

function getFilterClass(value: unknown): string {
  const bgClass = isFilterValueSelected(value) ? 'bg-slate-50' : 'bg-white'
  return `h-12 min-h-12 rounded-xl border-slate-200 ${bgClass} px-4 hover:bg-white focus:border-[rgb(21_151_255)] focus:ring-[rgb(21_151_255)]/15`
}
